<script setup lang="ts">
//#region imports
import { computed, nextTick, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { endOfDay, format, startOfDay } from 'date-fns';
import { useApi } from '@/api';
import { useLs } from '@/setup/vueLs';
import { useTypedStore } from '@/store';
import { useTablePaginator, useTableSearch, useTableSort } from '@/composables/useTableLS';
import UiPagination from '@/components/UIElements/UiPagination.vue';
import { useFormatCurrency } from '@/composables/useFormatCurrency';
import { useDetectPermissions } from '@/composables/useDetectPermissions';
import { usePopupManager } from '@/components/PopupManager.vue';
import { useEventBus } from '@/setup/eventBus';
import updateUrl from '@/service/updateUrl';
import _ from '@/setup/lodash';
import { useFilters } from '@/setup/filters';
import { useFormatters } from '@/composables/useFormatters';
import type { UiTableNewField, UiTableNewSortEmit } from '@/types/table';
import type { GetPaymentsHistoryData, GetPaymentsHistoryResponse } from '@/types/openapi-ts';
import type { TableMethods } from '@/types/core';
//#endregion

//#region types
type PaymentsHistoryRow = GetPaymentsHistoryResponse['payload']['data'][number];
type PaymentsHistoryPermissions = NonNullable<GetPaymentsHistoryResponse['misc']>['permissions'];
type PaymentsHistoryQuery = NonNullable<GetPaymentsHistoryData['query']>;
//#endregion

//#region const
const sizes = {
  1080: 20,
  1440: 30,
};

const fields: UiTableNewField<PaymentsHistoryRow>[] = [
  {
    name: 'incrementing_id',
    align: 'left',
    sortable: 'custom',
    width: '70',
    fixed: 'left',
  },
  {
    name: 'affiliate_email',
    align: 'left',
    sortable: 'custom',
    minWidth: '200',
    link: getAffiliateLink,
  },
  {
    name: 'team_name',
    width: '120',
    minWidth: '120',
  },
  {
    name: 'affiliate_referred_by_info',
    align: 'left',
    sortable: 'custom',
    minWidth: '200',
    link: getAffiliateReferredLink,
    computedValue: getAffiliateReferredEmail,
  },
  {
    name: 'amount',
    align: 'right',
    width: '92',
    class: 'bold',
    sortable: 'custom',
    computedValue: (row) => formatCurrencyValue(row.currency_code, row.amount),
    computedClass: getCurrencyClass,
  },
  {
    name: 'available_balance',
    align: 'right',
    width: '92',
    sortable: 'custom',
    computedValue: (row) => formatCurrencyValue(row.balance_currency_code, row.available_balance),
    computedClass: getCurrencyClass,
  },
  {
    name: 'hold_balance',
    align: 'right',
    width: '92',
    sortable: 'custom',
    computedValue: (row) => formatCurrencyValue(row.balance_currency_code, row.hold_balance),
    computedClass: getCurrencyClass,
  },
  {
    name: 'payment_period_end',
    align: 'left',
    width: '170',
    sortable: 'custom',
    computedValue: computePaymentPeriod,
  },
  {
    name: 'created_at',
    width: '120',
    align: 'left',
    headerAlign: 'left',
    sortable: 'custom',
    format: 'date-time',
    filterable: true,
    allowedOperators: ['≤', '=', '≥'],
  },
  {
    name: 'site_payment_method_name',
    align: 'center',
    headerAlign: 'center',
    width: '120',
    minWidth: '120',
    class: 'text',
    computedImg: (row) => row.site_payment_method_logo_base64,
    computedImgNull: (_, row) => row.site_payment_method_name,
  },
  {
    name: 'payment_wallet',
    align: 'left',
    headerAlign: 'center',
    width: '120',
    minWidth: '120',
    copyValue: true,
  },
];
//#endregion

//#region composables
const api = useApi();
const popupManager = usePopupManager();
const ls = useLs();
const store = useTypedStore();
const route = useRoute();
const { t } = useI18n();
const { checkCurrencyCode } = useFormatCurrency();
const { checkRequest, checkRoute } = useDetectPermissions();
const eventBus = useEventBus();
const filters = useFilters();
const { formatCurrencyValue } = useFormatters();

const { limit, count, page, setPage, setLsPathPage } = useTablePaginator({
  lsPath: getLsPath(),
  sizes,
  defaultSize: 15,
});

const { sortProp, sortOrder, setSort, optionsSort, setLsPath } = useTableSort({
  initialOptions:{
    sort_column: 'incrementing_id',
    sort_dir: 'desc',
  },
  lsPath: getLsPath(),
  fields,
});

const { searchQuery, setSearch } = useTableSearch();
//#endregion

//#region state
const tableRef = ref<TableMethods | null>(null);
const currentPaymentPageStatus = ref('');
const currencyCode = ref('');
const isDataLoading = ref(false);
const isFirstLoading = ref(true);
const tableData = ref<PaymentsHistoryRow>([]);
const permissions = ref<PaymentsHistoryPermissions>({});
const sitePaymentMethodId = ref('');

const columnsFilter = ref({});
const createdAtFrom = ref<string>();
const createdAtTo = ref<string>();
//#endregion

//#region computed
const adminAcl = computed(() => store.getters['auth/adminAcl']);
const paymentMethods = computed(() => store.getters['misc/paymentMethods']);
const lsPath = computed(() => getLsPath());
const customerCurrencies = computed(() => store.getters['misc/customerCurrencies']);
const allCurrencies = computed(() => {
  return [{ name: t('settings.currencies.allCurrencies'), code: '' }, ...customerCurrencies.value];
});
const filterParams = computed(() => {
  const params: Record<string, string> = {};
  if (sitePaymentMethodId.value) {
    params.site_payment_method_id = sitePaymentMethodId.value;
  }
  return params;
});
const filtersList = computed(() => ([
  {
    id: 'site_payment_method_id',
    name: 'sitePaymentMethod',
    type: 'select',
    params: {
      list: paymentMethods.value,
    },
  },
]));
//#endregion

//#region methods
function initData() {
  currentPaymentPageStatus.value = route.params.status as string;
  if (checkRequest('payments_view')){
    const _urlData = updateUrl.getParseParams(route.hash);

    if (!_.isEmpty(_urlData)) {
      setPage(_urlData);
      columnsFilter.value = _.get(_urlData, ['columns_filter']) || {};

      delete _urlData.page;
      delete _urlData.limit;
      delete _urlData.columns_filter;

      _.merge(optionsSort.value, _urlData);

      setSort(_urlData);
      setSearch(_urlData);

      const currency_code = _.get(_urlData, ['currency_code']);
      if(currency_code) {
        currencyCode.value = checkCurrencyCode(currency_code as string);
      }
      sitePaymentMethodId.value = _.get(_urlData, ['site_payment_method_id'], '') as string;
    } else {
      const currency_code = ls.get(`${lsPath.value}/currency_code`);
      if(currency_code) {
        currencyCode.value = checkCurrencyCode(currency_code as string);
      }
      columnsFilter.value = ls.get(`${lsPath.value}/columns_filter`) || {};
    }

    parseFilter(columnsFilter.value, _.isEmpty(columnsFilter.value) === true ? 'reset' : undefined);

    nextTick(() => {
      fetchData();
    });
  }
  checkRoute('payments_view');
}

const _query = () => {
  let result = {
    ...optionsSort.value,
    search: searchQuery.value,
    limit: limit.value,
    offset: limit.value * page.value - limit.value,
    moderation_status: currentPaymentPageStatus.value,
    currency_code: currencyCode.value,
    site_payment_method_id: sitePaymentMethodId.value,
    type: 'internal',
    created_at_from: createdAtFrom.value,
    created_at_to: createdAtTo.value,
  } as PaymentsHistoryQuery;

  if(sortProp.value === 'affiliate_referred_by_info') {
    result.sort_column = 'affiliate_referred_by_email';
  }

  return result;
};

const _completedFilterUrl = () => {
  const _dataFilters = {
    ..._.omit(_query(), ['offset', 'created_at_from', 'created_at_to']),
    columns_filter: columnsFilter.value,
  };
  updateUrl.updateGetParams(_dataFilters);
};

async function fetchData(silent = false) {
  if (!silent && !isFirstLoading.value) {
    isDataLoading.value = true;
  }

  _completedFilterUrl();
  let options = _query();

  try {
    const { data: { payload, misc } } = await api.getPaymentsHistory(options);

    count.value = misc!.count;
    permissions.value = misc!.permissions;
    tableData.value = payload.data;
  } finally {
    if(!isFirstLoading.value) {
      isDataLoading.value = false;
    } else {
      isFirstLoading.value = false;
    }

    if (tableRef.value) {
      tableRef.value.setColumnFilters(columnsFilter.value);
    }
  }
}

const pageChange: InstanceType<typeof UiPagination>['$props']['onPage-change'] = (pageVal, size) => {
  if (page.value !== pageVal || limit.value !== size) {
    page.value = pageVal;
    limit.value = size;
    fetchData();
  }
};

const handleSort = ({ prop, order }: UiTableNewSortEmit) => {
  if (order === '') return;
  if (sortProp.value !== prop) {
    sortProp.value = prop;
  } else {
    sortOrder.value = order;
  }
  nextTick(() => {
    fetchData();
  });
};

const handleRefresh = () => {
  page.value = 1;
  fetchData();
};

const handlePaymentsFilter = (data: Record<string, string> = {}) => {
  sitePaymentMethodId.value = data.site_payment_method_id || '';
  page.value = 1;
  fetchData();
};

function changeCurrency(value: string) {
  currencyCode.value = value;
  ls.set(`${lsPath.value}/currency_code`, value);
  fetchData();
}

function reviewActions(row: PaymentsHistoryRow) {
  const isUser = !adminAcl.value.is_superuser && permissions.value && permissions.value[row.id]!.review_actions;
  const isSuperUser = adminAcl.value.is_superuser;
  return isSuperUser || isUser;
}

function handleRowClick(row: PaymentsHistoryRow) {
  return `/payments/info/${row.id}?from=payments_moderation`;
}

function handleDeclineClick(row: PaymentsHistoryRow, e: MouseEvent) {
  e.preventDefault();
  e.stopPropagation();

  const context = popupManager.confirm.open({
    data: {
      title: t('payments.confirm.title_4'),
      message: t('payments.confirm.msg_4', { affiliateEmail: row.affiliate_email }),
      bodyIcon: 'exclamation-triangle',
      bodyIconColor: 'var(--text-negative)',
      confirmTitle: t('payments.confirm.action_4'),
      confirmType: 'negative',
      cancelTitle: t('affiliates.cardView.toolbar.btns.close'),
    },
    listeners: {
      success: async () => {
        const confirm = await context;
        await declinePayment(row);
        confirm.handleClose();
      },
    },
  });
}

function declinePayment(row: PaymentsHistoryRow) {
  isDataLoading.value = true;
  return api
    .declinePayment(row.id)
    .then(() => {
      fetchData();
    })
    .catch((error) => {
      isDataLoading.value = false;
      eventBus.$emit('notify-event', {
        type: 'error',
        text: t('payments.noty.req_fail', { statusCode: error.status }),
      });
    });
}

function openPaymentEstimates(currencyCode: string) {
  popupManager.paymentEstimates.open({ data: { currencyCode } });
}

function getLsPath() {
  return `PaymentsReview/${route.params.status}`;
}

function getAffiliateLink(row: PaymentsHistoryRow) {
  return `/affiliates/${row.affiliate_id}/info`;
}

function getAffiliateReferredEmail(row: PaymentsHistoryRow) {
  if (row.affiliate_referred_by_info === null) {
    return '';
  }
  return row.affiliate_referred_by_info.email;
}

function getAffiliateReferredLink(row: PaymentsHistoryRow) {
  if (row.affiliate_referred_by_info !== null) {
    return row.affiliate_referred_by_info.permissions.view
      ? `/affiliates/${row.affiliate_referred_by_info.id}/info`
      : '';
  }
  return '';
}

function getCurrencyClass(value: number) {
  return value < 0 ? 'negative' : '';
}

function computePaymentPeriod(row: PaymentsHistoryRow) {
  return row.payment_period_start && row.payment_period_end
    ? `${filters.formatDate4(row.payment_period_start)} - ${filters.formatDate4(row.payment_period_end)}`
    : '';
}

function handlerFilter(filter: any, action: string) {
  parseFilter(filter, action);
  fetchData();
}

function parseFilter(filter: any, action?: string) {
  const columnProp = 'created_at';
  if (action === 'reset') {
    createdAtFrom.value = undefined;
    createdAtTo.value = undefined;
    columnsFilter.value = {};
    ls.set(`${lsPath.value}/columns_filter`, {});
    return;
  }

  if (filter[columnProp].op === '≥') {
    createdAtFrom.value = format(startOfDay(filter[columnProp].dateRange),'yyyy-MM-dd HH:mm:ss');
    createdAtTo.value = undefined;
  } else if (filter[columnProp].op === '≤') {
    createdAtFrom.value = undefined;
    createdAtTo.value = format(endOfDay(filter[columnProp].dateRange),'yyyy-MM-dd HH:mm:ss');
  } else {
    createdAtFrom.value = format(startOfDay(filter[columnProp].dateRange[0]),'yyyy-MM-dd HH:mm:ss');
    createdAtTo.value = format(endOfDay(filter[columnProp].dateRange[1]),'yyyy-MM-dd HH:mm:ss');
  }
  ls.set(`${lsPath.value}/columns_filter`, filter);
  columnsFilter.value = filter;
}
//#endregion

//#region watch
watch(lsPath, (value) => {
  currentPaymentPageStatus.value = route.params.status as string;
  currencyCode.value = '';

  setLsPath(value);
  setLsPathPage(value);

  searchQuery.value = '';

  const currency = ls.get(`${lsPath.value}/currency_code`);
  if (currency !== '' && currency !== null) {
    currencyCode.value = checkCurrencyCode(ls.get(`${lsPath.value}/currency_code`));
  }

  parseFilter(columnsFilter.value, _.isEmpty(columnsFilter.value) === true ? 'reset' : undefined);
  nextTick(() => {
    fetchData();
  });

  popupManager.$scopeDispose();
});
//#endregion

//#region hooks
initData();
//#endregion
</script>

<template>
  <div class="payments-review">
    <UiToolbar>
      <template v-slot:title>
        <div class="t:flex t:items-center t:gap-8">
          {{ $t('payments.page_title.payments_moderation.name') }}
          <UiSeparator
            type="dot"
          />
          <UiStatusText
            :type="currentPaymentPageStatus"
            :text="$t(`payments.page_title.payments_moderation.${currentPaymentPageStatus}`)"
            :font-size="20"
          />
        </div>
      </template>
      <template v-slot:left-slot>
        <UiInput
          v-model.trim="searchQuery"
          :width="180"
          is-search
          @search="handleRefresh"
        />
        <DefaultFilter
          :model-value="filterParams"
          :filters-list="filtersList"
          @submit="handlePaymentsFilter"
        />
      </template>
      <template v-slot:right-slot>
        <UiCurrencyPicker
          :key="lsPath"
          :model-value="currencyCode"
          :currencies="allCurrencies"
          :disabled="isDataLoading"
          @update:model-value="changeCurrency"
        />
        <UiButton
          v-if="currentPaymentPageStatus === 'on_review'"
          is-icon
          icon="database"
          @click="openPaymentEstimates(currencyCode)"
        />
        <UiPagination
          :page="page"
          :page-size="limit"
          :count="count"
          show-size-select
          @page-change="pageChange"
        />
      </template>
    </UiToolbar>
    <UiTableNew
      ref="tableRef"
      v-loading="isDataLoading"
      :fields="fields"
      :data="tableData"
      :rows-count="limit"
      :sort="{
        prop: sortProp,
        order: sortOrder,
      }"
      lazy-loading
      is-static
      i18n-path="payments.review"
      @sort="handleSort"
      @filter="handlerFilter"
    >
      <template v-slot:append>
        <ElTableColumn
          :resizable="false"
          fixed="right"
          align="center"
          header-align="center"
        >
          <template v-slot:header="scope">
            <div
              :key="scope.$index"
              class="table-action-header"
            >
              {{ $t('settings.tokens.actions') }}
            </div>
          </template>
          <template v-slot="scope">
            <ActionIcon
              icon="eye"
              :tooltip="$t('payments.history.info')"
              :router-to="handleRowClick(scope.row)"
            />
            <ActionIcon
              v-if="currentPaymentPageStatus === 'on_review' && reviewActions(scope.row)"
              icon="times"
              color="var(--text-negative)"
              :tooltip="$t('payments.history.decline')"
              @click="handleDeclineClick(scope.row, $event)"
            />
          </template>
        </ElTableColumn>
      </template>
    </UiTableNew>
  </div>
</template>
